import { Skeleton } from "@mui/material";
import {
  Box,
  ConfirmationModal,
  GKButton,
  Grid2,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import ConsentOrderCard from "./ConsentOrderCard";
import OrderResultTable from "./OrderResultTable";
import PaymentMethodSelector, { PaymentMethod } from "./PaymentMethodSelector";
import TermsAndConditions from "./TermsAndConditions";
import { PlaceResultItem, useMFConsent } from "./useMFConsent";

interface Props {
  logo: string;
  orderId: string | null;
  clientId: string | null;
  portfolioId: string | null;
}

const Frame = ({
  logo,
  children,
}: {
  logo: string;
  children: React.ReactNode;
}) => (
  <Grid2
    container
    bgcolor="#F8FAFC"
    height="100vh"
    direction="column"
    width="100%"
  >
    <Grid2
      size="auto"
      py={2}
      px={{ xs: 2, sm: 4 }}
      display="flex"
      alignItems="center"
      borderBottom="1px solid var(--neutral-100)"
    >
      <img
        src={logo || "/Logos/iconFullLogo.png"}
        alt=""
        height="-webkit-fill-available"
        width="auto"
        style={{ maxHeight: "32px" }}
        onError={({ currentTarget }) => {
          currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
        }}
      />
    </Grid2>

    <Grid2
      container
      size={12}
      flex={1}
      justifyContent="center"
      sx={{ overflowY: "auto" }}
      p={2}
    >
      <Box width="100%" maxWidth="720px">
        {children}
      </Box>
    </Grid2>

    <Grid2
      size="auto"
      p={2}
      display="flex"
      justifyContent="center"
      borderTop="1px solid #E3E8EF"
    >
      <Typography variant="caption" color="text.secondary">
        © {dayjs().get("year")} gridkey. All rights reserved.
      </Typography>
    </Grid2>
  </Grid2>
);

const CenteredCard = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => (
  <Box
    bgcolor="var(--white)"
    border="1px solid #E3E8EF"
    borderRadius="16px"
    p="32px 40px"
    textAlign="center"
    mt={4}
  >
    <Typography fontSize={20} fontWeight={600}>
      {title}
    </Typography>
    <Typography
      fontSize={13}
      fontWeight={500}
      color="var(--neutral-500)"
      mt={1}
    >
      {message}
    </Typography>
  </Box>
);

const Orders = ({ logo, orderId, clientId, portfolioId }: Props) => {
  const form = useFormContext();
  const token = form.getValues("token");
  const consentAccepted = useWatch({
    control: form.control,
    name: "consentAccepted",
  });
  const paymentType = useWatch({
    control: form.control,
    name: "paymentType",
  });

  const [declineOpen, setDeclineOpen] = useState(false);
  const [action, setAction] = useState<"confirm" | "reject" | null>(null);
  const [resultItems, setResultItems] = useState<PlaceResultItem[] | null>(
    null,
  );

  const { consentQuery, placeMutation } = useMFConsent({
    orderId,
    clientId,
    portfolioId,
    token,
  });

  const submitted = placeMutation.isSuccess;
  const submitting = placeMutation.isLoading;
  const confirmed = placeMutation.isSuccess && action === "confirm";
  const declined = placeMutation.isSuccess && action === "reject";

  const handleConfirm = () => {
    setAction("confirm");
    placeMutation.mutate(
      { consent: true, paymentType: paymentType || null },
      {
        onSuccess: (response: any) => {
          const items = response?.data?.data?.items ?? [];
          setResultItems(items);
          toast.success(response?.data?.message || "Order placed.");
        },
        onError: (error: any) => {
          setAction(null);
          toast.error(
            error?.response?.data?.message ||
              "Could not place the order. Please try again.",
          );
        },
      },
    );
  };

  const handleReject = () => {
    setAction("reject");
    placeMutation.mutate(
      { consent: false },
      {
        onSuccess: () => toast.success("Order declined."),
        onError: () => {
          setAction(null);
          toast.error("Could not decline the order. Please try again.");
        },
      },
    );
  };

  if (consentQuery.isLoading) {
    return (
      <Frame logo={logo}>
        <Box display="flex" flexDirection="column" gap="16px" mt={2}>
          <Skeleton variant="rounded" height={140} />
          <Skeleton variant="rounded" height={140} />
        </Box>
      </Frame>
    );
  }

  if (consentQuery.isError) {
    return (
      <Frame logo={logo}>
        <CenteredCard
          title="This link is no longer valid"
          message="The order could not be loaded. The link may have expired or already been actioned."
        />
      </Frame>
    );
  }

  // After placing, show the per-item result (payment links / errors).
  if (resultItems) {
    const anyPayable = resultItems.some(
      (it) =>
        (it.errorCode === "200" || it.status === "success") &&
        it.paymentTokenUrl,
    );
    return (
      <Frame logo={logo}>
        <Box display="flex" flexDirection="column" gap="24px" mt={2} pb={2}>
          <Box>
            <Typography fontSize={20} fontWeight={600}>
              Order summary
            </Typography>
            <Typography
              fontSize={13}
              fontWeight={500}
              color="var(--neutral-500)"
              mt={0.5}
            >
              {anyPayable
                ? "Your order is placed. Complete the payment for each scheme below."
                : "Here is the status of your order."}
            </Typography>
          </Box>
          <OrderResultTable items={resultItems} />
        </Box>
      </Frame>
    );
  }

  const { data } = consentQuery;
  const order = data?.order;
  const items = order?.items ?? data?.items ?? [];

  if (!data || !order || items.length === 0) {
    return (
      <Frame logo={logo}>
        <CenteredCard
          title="No orders to review"
          message="There are no pending orders associated with this link."
        />
      </Frame>
    );
  }

  return (
    <Frame logo={logo}>
      <Box display="flex" flexDirection="column" gap="24px" mt={2} pb={2}>
        <Box>
          <Typography fontSize={20} fontWeight={600}>
            Review &amp; confirm your order
          </Typography>
          <Typography
            fontSize={13}
            fontWeight={500}
            color="var(--neutral-500)"
            mt={0.5}
          >
            Please review the order details below before placing them.
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap="16px">
          {items.map((item) => (
            <ConsentOrderCard key={item.itemId} order={order} item={item} />
          ))}
        </Box>

        <Show.When isTrue={order.orderType === "purchase"}>
          <PaymentMethodSelector
            value={paymentType}
            onChange={(v: PaymentMethod) =>
              form.setValue("paymentType", v, { shouldDirty: true })
            }
            disabled={submitted}
          />
        </Show.When>

        <TermsAndConditions
          terms={data.consentStatement}
          disabled={submitted}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <Show.When isTrue={!submitted}>
            <GKButton
              variant="outlined"
              color="error"
              disabled={submitting}
              onClick={() => setDeclineOpen(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Decline
            </GKButton>
            <GKButton
              variant="contained"
              disabled={
                !consentAccepted ||
                submitting ||
                (order.orderType === "purchase" && !paymentType)
              }
              loading={submitting && action === "confirm"}
              onClick={handleConfirm}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Place Order
            </GKButton>
          </Show.When>
          <Show.When isTrue={confirmed}>
            <Typography
              fontSize={14}
              fontWeight={600}
              color="var(--success-700)"
            >
              Order placed successfully.
            </Typography>
          </Show.When>
          <Show.When isTrue={declined}>
            <Typography
              fontSize={14}
              fontWeight={600}
              color="var(--neutral-500)"
            >
              Order declined.
            </Typography>
          </Show.When>
        </Box>
      </Box>

      <ConfirmationModal
        open={declineOpen}
        setOpen={setDeclineOpen}
        title="Decline Order"
        customButtonText="Decline"
        messageContent="Are you sure you want to decline this order? This cannot be undone."
        onConfirm={handleReject}
      />
    </Frame>
  );
};

export default Orders;
